import {
	PropsWithChildren,
	createContext,
	startTransition,
	useContext,
	useEffect,
	useState,
} from 'react';

import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	UserProfile,
	type ExtendedProfileType,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import { Cookie } from './cookie';

export type LoggedInUser = Partial<UserProfile> & {
	isUserLoggedIn: boolean;
	requestInProgress: boolean;
	extended_profile?: ExtendedProfileType;
};

const DEFAULT_USER_STATE: LoggedInUser = {
	isUserLoggedIn: false,
	requestInProgress: true,
};

const LoggedInUserContext = createContext<LoggedInUser>({
	...DEFAULT_USER_STATE,
});

/**
 * Custom hook to return LoggedInUserContext.
 * Returns `DEFAULT_USER_STATE` by default until the user profile is fetched via `stargateClient.me()`.
 * @returns @type {LoggedInUser}
 */
export const useLoggedInUser = (): LoggedInUser => {
	const context = useContext(LoggedInUserContext);
	if (!context) {
		throw new Error('calls to useLoggedInUser must be wrapped in <LoggedInUserProvider>');
	}

	return context;
};

export const LoggedInUserProvider = ({ children }: PropsWithChildren) => {
	const stargateClient = useBxpWebStargateClient();
	const [loggedInUserInstance, setLoggedInUserInstance] = useState<LoggedInUser>({
		...DEFAULT_USER_STATE,
	});

	useEffect(() => {
		startTransition(() => {
			// Check for login_user_detected cookie first (set during SSR).
			// As fallback, check for cloud.session.token directly (handles static fallback case).
			const loginUserDetectedCookie = Cookie.readCookie('login_user_detected') === 'true';
			const cloudSessionToken =
				Cookie.readCookie('cloud.session.token') || Cookie.readCookie('cloud.session.token.stg');
			const isUserLoggedIn = loginUserDetectedCookie || cloudSessionToken !== null;

			if (!isUserLoggedIn) {
				// If logged in user cookie isn't present, then we don't need to make a call to stargate.
				// This cookie is set by bxp-gateway to determine if the user is logged in or not.
				setLoggedInUserInstance({
					...DEFAULT_USER_STATE,
					requestInProgress: false,
				});
				return;
			}

			const getUserProfile = async () => {
				return await stargateClient.me({});
			};

			getUserProfile()
				.then((userProfile) => {
					setLoggedInUserInstance({
						requestInProgress: false,
						isUserLoggedIn: true,
						...userProfile,
					});
				})
				.catch((e) => {
					console.warn('Error getting user profile: ', e);
					setLoggedInUserInstance({
						...DEFAULT_USER_STATE,
						requestInProgress: false,
					});
				});
		});
	}, [stargateClient]);

	return (
		<LoggedInUserContext.Provider value={loggedInUserInstance}>
			{children}
		</LoggedInUserContext.Provider>
	);
};

type MockLoggedInUserProviderProps = PropsWithChildren & {
	loggedInUser: LoggedInUser;
};

export const MockLoggedInUserProvider = ({
	children,
	loggedInUser,
}: MockLoggedInUserProviderProps) => (
	<LoggedInUserContext.Provider value={loggedInUser}>{children}</LoggedInUserContext.Provider>
);
